import React, { useState } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  makeStyles,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@material-ui/core'
import { Edit, Delete } from '@material-ui/icons'
import { useTranslate } from 'react-admin'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import AddButton from '../ui/AddButton/AddButton'
import AddTeamModal from '../ui/AddTeamModal/AddTeamModal'
import AssignTeamModal from '../ui/AssignTeamModal/AssignTeamModal'
import DeleteConfirmationModal from '../ui/DeleteConfirmationModal/DeleteConfirmationModal'
import useTeamsSettings from '../../hooks/useTeamsSettings'
import useHistoryPush from '../../hooks/useHistoryPush'
import assignTeamToSeason from '../../Apis/season/assignTeamToSeason'
import removeTeamFromSeason from '../../Apis/season/removeTeamFromSeason'
import getTeams from '../../Apis/team/getTeams'
import getSeasonTeams from '../../Apis/season/getSeasonTeams'
import QueryKeys from '../../utils/constants/queryKeys'
import COLORS from '../../utils/constants/colors'

const useStyles = makeStyles((theme) => ({
  card: {
    margin: theme.spacing(3),
  },
  cardTitle: {
    fontWeight: 'bold',
  },
  cardSubtitle: {
    color: COLORS.lightGrey,
  },
  divider: {
    marginBottom: theme.spacing(2),
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  clickableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  teamNameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
  },
  teamLogo: {
    width: 32,
    height: 32,
    borderRadius: '50%',
    objectFit: 'cover',
  },
}))

const TeamsPage = () => {
  const classes = useStyles()
  const translate = useTranslate()
  const { redirect } = useHistoryPush()
  const queryClient = useQueryClient()
  const {
    addSeasonTeam,
    updateTeam,
    deleteTeam,
    showAddTeamModal,
    setShowAddTeamModal,
    seasonId,
    orgId,
  } = useTeamsSettings()

  // Fetch season teams instead of org teams
  const { data: teams } = useQuery(
    [QueryKeys.GET_TEAMS, orgId, seasonId],
    () => getSeasonTeams(orgId, seasonId),
    {
      enabled: !!orgId && !!seasonId,
    }
  )

  const [editingTeam, setEditingTeam] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [teamToDelete, setTeamToDelete] = useState(null)
  const [showAssignTeamModal, setShowAssignTeamModal] = useState(false)
  const [availableTeams, setAvailableTeams] = useState([])
  const [isLoadingTeams, setIsLoadingTeams] = useState(false)

  const handleAddTeam = () => {
    setEditingTeam(null)
    setShowAddTeamModal(true)
  }

  const handleEditTeam = (seasonTeamId) => {
    const team = teams.find((t) => t.id === seasonTeamId)
    if (team) {
      setEditingTeam(team)
      setShowAddTeamModal(true)
    }
  }

  const handleCloseModal = () => {
    setShowAddTeamModal(false)
    setEditingTeam(null)
  }

  const handleDeleteTeam = (seasonTeamId) => {
    const team = teams.find((t) => t.id === seasonTeamId)
    if (team) {
      setTeamToDelete(team)
      setShowDeleteModal(true)
    }
  }

  const onDeleteTeam = () => {
    if (teamToDelete) {
      deleteTeam.mutate(
        { seasonTeamId: teamToDelete.seasonTeamId },
        {
          onSuccess: () => {
            setShowDeleteModal(false)
            setTeamToDelete(null)
          },
        }
      )
    }
  }

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false)
    setTeamToDelete(null)
  }

  const handleRowClick = (teamId) => {
    redirect(`/seasonTeams/${teamId}`)
  }

  const handleAssignTeam = async () => {
    setIsLoadingTeams(true)
    try {
      const teamsData = await getTeams(orgId)
      setAvailableTeams(teamsData || [])
      setShowAssignTeamModal(true)
    } catch (error) {
      console.error('Error fetching teams:', error)
    } finally {
      setIsLoadingTeams(false)
    }
  }

  const assignTeamMutation = useMutation(
    (data) => assignTeamToSeason(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKeys.GET_TEAMS)
      },
    }
  )

  const removeTeamMutation = useMutation(
    (data) => removeTeamFromSeason(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKeys.GET_TEAMS)
      },
    }
  )

  const handleSelectTeam = (teamId, seasonTeamId) => {
    if (seasonTeamId) {
      removeTeamMutation.mutate({
        orgId,
        seasonTeamId,
      })
    } else {
      assignTeamMutation.mutate({
        orgId,
        seasonId,
        teamId,
      })
    }
  }

  return (
    <Card className={classes.card}>
      <CardHeader
        title={
          <div className={classes.headerContainer}>
            <div>
              <Typography className={classes.cardTitle} variant='h5'>
                Teams
              </Typography>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <AddButton label='Assign Team' onClick={handleAssignTeam} />
              <AddButton label='Add Season Team' onClick={handleAddTeam} filled />
            </div>
          </div>
        }
      />
      <CardContent>
        <Divider variant='middle' className={classes.divider} />
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Team Name</TableCell>
                <TableCell>Identifier</TableCell>
                <TableCell align='right' padding='none' style={{ paddingRight: 16 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {teams && teams.map((team) => (
                <TableRow
                  key={team.id}
                  className={classes.clickableRow}
                  onClick={() => handleRowClick(team.teamId)}
                >
                  <TableCell>
                    <div className={classes.teamNameCell}>
                      {team.teamLogoUrl && (
                        <img
                          src={team.teamLogoUrl}
                          alt={team.teamName}
                          className={classes.teamLogo}
                        />
                      )}
                      {team.teamName}
                    </div>
                  </TableCell>
                  <TableCell>{team.threeLetterIdentifier}</TableCell>
                  <TableCell align='right' padding='none' style={{ paddingRight: 16 }}>
                      <IconButton
                        size='small'
                        color='primary'
                        onClick={(e) => {
                          e.stopPropagation()
                          handleEditTeam(team.id)
                        }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size='small'
                        color='secondary'
                        onClick={(e) => {
                          e.stopPropagation()
                          handleDeleteTeam(team.id)
                        }}
                      >
                        <Delete />
                      </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
      <AddTeamModal
        showAddTeamModal={showAddTeamModal}
        handleCloseModal={handleCloseModal}
        addTeam={addSeasonTeam}
        updateTeam={updateTeam}
        teamData={editingTeam}
        seasonId={seasonId}
      />
      <DeleteConfirmationModal
        isModalOpen={showDeleteModal}
        onClose={handleCloseDeleteModal}
        title={translate('ra.text.deleteTeam')}
        subtitle={translate('ra.text.deleteTeamName')}
        comparisonValue={teamToDelete?.name}
        buttonLabel={translate('ra.action.delete')}
        textFieldLabel={translate('ra.formFields.teamName')}
        onSubmit={onDeleteTeam}
        isLoading={deleteTeam.isLoading}
      />
      <AssignTeamModal
        isOpen={showAssignTeamModal}
        onClose={() => setShowAssignTeamModal(false)}
        teams={availableTeams}
        seasonTeams={teams}
        onSelectTeam={handleSelectTeam}
        isLoading={isLoadingTeams || assignTeamMutation.isLoading || removeTeamMutation.isLoading}
      />
    </Card>
  )
}

export default TeamsPage
